import { fmt, signedMoney, pnlColor, timeOf } from "../lib/format";

export const TradeHistory = ({ trades }) => {
  const list = trades || [];
  if (!list.length)
    return <div className="flex-1 flex items-center justify-center text-zinc-600 text-[11px]">No trades taken yet</div>;

  const closed = list.filter((t) => t.status === "CLOSED");
  const open = list.filter((t) => t.status === "OPEN");
  const rejected = list.filter((t) => t.status === "REJECTED");
  const realized = closed.reduce((a, t) => a + (t.pnl || 0), 0);
  const wins = closed.filter((t) => (t.pnl || 0) >= 0).length;
  const winrate = closed.length ? Math.round((wins / closed.length) * 100) : 0;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* summary strip */}
      <div className="grid grid-cols-5 gap-1 px-2 py-1.5 border-b border-zinc-800 text-center flex-shrink-0" data-testid="trades-summary">
        <div>
          <div className="text-[8px] uppercase tracking-wide text-zinc-500">Realized</div>
          <div className={`text-[11px] font-bold font-mono ${pnlColor(realized)}`} data-testid="trades-total-pnl">{signedMoney(realized)}</div>
        </div>
        <div>
          <div className="text-[8px] uppercase tracking-wide text-zinc-500">Win %</div>
          <div className="text-[11px] font-bold font-mono text-zinc-200">{winrate}%</div>
        </div>
        <div>
          <div className="text-[8px] uppercase tracking-wide text-zinc-500">Closed</div>
          <div className="text-[11px] font-bold font-mono text-zinc-200">{closed.length}</div>
        </div>
        <div>
          <div className="text-[8px] uppercase tracking-wide text-zinc-500">Open</div>
          <div className="text-[11px] font-bold font-mono text-amber-400">{open.length}</div>
        </div>
        <div>
          <div className="text-[8px] uppercase tracking-wide text-zinc-500">Rejected</div>
          <div className={`text-[11px] font-bold font-mono ${rejected.length ? "text-red-400" : "text-zinc-500"}`} data-testid="trades-rejected-count">{rejected.length}</div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto thin-scroll">
        <table className="w-full text-[11px]">
          <thead className="text-[9px] uppercase text-zinc-500 sticky top-0 bg-[#09090b]">
            <tr className="border-b border-zinc-800">
              <th className="text-left font-medium px-2 py-1">Time</th>
              <th className="text-left font-medium px-1 py-1">Sym</th>
              <th className="text-left font-medium px-1 py-1">Side</th>
              <th className="text-right font-medium px-1 py-1">Qty</th>
              <th className="text-right font-medium px-1 py-1">Entry</th>
              <th className="text-right font-medium px-1 py-1">Exit</th>
              <th className="text-right font-medium px-2 py-1">P&L</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {list.map((t) => t.status === "REJECTED" ? (
              <tr key={t.id} data-testid={`trade-rejected-${t.id}`} className="border-b border-red-900/40 bg-red-950/20">
                <td className="px-2 py-1 text-zinc-500 whitespace-nowrap">{timeOf(t.entry_at)}</td>
                <td className="px-1 py-1 font-bold text-zinc-100">{t.symbol || "—"}<span className="ml-1 text-[7px] px-1 rounded bg-red-500/20 text-red-400">REJ</span></td>
                <td className={`px-1 py-1 ${t.direction === "LONG" ? "text-green-500" : t.direction === "SHORT" ? "text-red-500" : "text-zinc-500"}`}>{t.direction || "—"}</td>
                <td className="px-1 py-1 text-right text-zinc-400">{t.qty ?? "—"}</td>
                <td colSpan={3} className="px-2 py-1 text-left text-red-300 whitespace-normal break-words leading-tight" data-testid="trade-rejected-reason" title={t.reason}>
                  <span className="text-[8px] uppercase tracking-wide text-red-500/80 mr-1">{t.source || "broker"}</span>{t.reason}
                </td>
              </tr>
            ) : (
              <tr key={t.id} data-testid={`trade-${t.id}`} className="border-b border-zinc-800/60 hover:bg-zinc-800/30">
                <td className="px-2 py-1 text-zinc-500 whitespace-nowrap">{timeOf(t.closed_at || t.entry_at)}</td>
                <td className="px-1 py-1 font-bold text-zinc-100">
                  {t.symbol}
                  <span className={`ml-1 text-[7px] px-1 rounded ${t.mode === "LIVE" ? "bg-green-500/20 text-green-400" : "bg-zinc-700/50 text-zinc-400"}`}>{t.mode === "LIVE" ? "L" : "P"}</span>
                </td>
                <td className={`px-1 py-1 ${t.direction === "LONG" ? "text-green-500" : "text-red-500"}`}>{t.direction}</td>
                <td className="px-1 py-1 text-right text-zinc-400">{t.qty}</td>
                <td className="px-1 py-1 text-right text-zinc-300">{fmt(t.entry_price)}</td>
                <td className="px-1 py-1 text-right text-zinc-300">{t.status === "OPEN" ? "—" : fmt(t.exit_price)}</td>
                <td className="px-2 py-1 text-right font-semibold">
                  {t.status === "OPEN"
                    ? <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400">OPEN</span>
                    : <span className={pnlColor(t.pnl)}>{signedMoney(t.pnl)}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
